import { ApplicationInsights, SeverityLevel } from '@microsoft/applicationinsights-web';
import { v4 as uuidv4 } from 'uuid';
import store from 'app/store';
import { parameters } from 'api/parameter';

// Shown as cloud_RoleName, so browser telemetry can be told apart from the Function app in the shared resource.
const CLOUD_ROLE_NAME = 'Briisk.App.B2C.Whitelabel';

const CLIENT_SESSION_STORAGE_KEY = 'clientSessionId';

// Query parameters whose values are safe and useful for diagnosis; every other value is redacted.
const ALLOWED_QUERY_PARAMS = ['token', 'site', 'providerMessage'];

// Path segments that carry one-time tokens (resume links, agent OTP keys) and must never reach the logs.
const SENSITIVE_PATH_PATTERNS = [/(\/resume-[a-z]+\/[^/?#]+\/[^/?#]+\/)[^/?#]+/gi, /(\/AgentGenerateOtp\/)[^/?#]+/gi];

// App Insights records every API call, which costs money and hides the calls that matter.
// So failed calls are always kept, but successful ones only for the steps listed here:
// payment, policy creation, policy approval/rejection, and the app startup calls.
const KEPT_DEPENDENCY_PATTERNS = [
	/\/Payment\//i,
	/\/Policy\/(CreatePolicySale|UpdatePolicySale|EndorseQuotePolicySale)/i,
	/\/Policy\/(ApprovePolicy|RejectPolicy|MakeSalePayment|EndorseIssuePolicySale)/i,
	/\/Channel\/(Details|Products)\//i
];

// Captured when the bundle loads, before setParam rewrites the query string, so it shows exactly how the user
// arrived (e.g. what the payment gateway sent back on the return URL).
const landingLocation = { pathname: window.location.pathname, search: window.location.search };

let appInsights = null;
let clientSessionId;
let lastPageViewPathname;

// An empty value or an unsubstituted pipeline token (local builds) means logging is not configured.
const isConfigured = connectionString =>
	typeof connectionString === 'string' && connectionString.trim() !== '' && !connectionString.startsWith('[[');

// Redacts tokens in known sensitive path segments and every query value that is not allow-listed.
const scrubUrl = value => {
	if (typeof value !== 'string') return value;
	const withoutTokens = SENSITIVE_PATH_PATTERNS.reduce(
		(result, pattern) => result.replace(pattern, '$1[redacted]'),
		value
	);
	return withoutTokens.replace(/([?&])([^=&#]+)=([^&#]*)/g, (match, separator, key) =>
		ALLOWED_QUERY_PARAMS.includes(key) ? match : `${separator}${key}=[redacted]`
	);
};

// Returns only the origin, because full third-party URLs (e.g. the payment gateway) can carry session ids.
export const getUrlOrigin = url => {
	try {
		return url ? new URL(url).origin : undefined;
	} catch (error) {
		return undefined;
	}
};

// Cross-origin referrers are usually the payment gateway, so only their origin is kept.
const scrubReferrer = value => {
	const origin = getUrlOrigin(value);
	return origin && origin !== window.location.origin ? origin : scrubUrl(value);
};

// Kept in sessionStorage so it survives the same-tab round trip to the payment gateway,
// which lets logs from before and after the redirect be linked even when the redux state is lost.
const getClientSessionId = () => {
	if (clientSessionId) return clientSessionId;
	try {
		clientSessionId = sessionStorage.getItem(CLIENT_SESSION_STORAGE_KEY) || uuidv4();
		sessionStorage.setItem(CLIENT_SESSION_STORAGE_KEY, clientSessionId);
	} catch (error) {
		// Storage can be blocked (e.g. partitioned iframes); a per-page id still groups this page's logs.
		clientSessionId = clientSessionId || uuidv4();
	}
	return clientSessionId;
};

// Added to every telemetry item so any row can be traced to its brand-label site and browser session.
const getBaseContext = () => {
	const form = store?.getState()?.fuse?.form;
	return {
		environment: parameters.environment,
		host: window.location.host,
		hostPrefix: form?.mySite?.hostPrefix,
		channelInstanceId: form?.channelSettings?.value?.channel?.instanceId,
		clientSessionId: getClientSessionId(),
		path: scrubUrl(window.location.pathname)
	};
};

// Exceptions carry the policy references so they can be joined with backend logs that mention the same references.
const getPolicyContext = () => {
	const createPolicyResponse = store?.getState()?.fuse?.form?.createPolicyResponse;
	return {
		policyReference: createPolicyResponse?.policies?.[0]?.policyReference,
		policySaleReference: createPolicyResponse?.policySaleReference
	};
};

const telemetryInitializer = item => {
	try {
		if (item.baseType === 'RemoteDependencyData') {
			const { success, name, target, data } = item.baseData || {};
			// Failed calls are always kept app-wide; successful ones only for the payment and bootstrap endpoints.
			if (
				success !== false &&
				!KEPT_DEPENDENCY_PATTERNS.some(pattern => pattern.test(`${name} ${target} ${data}`))
			) {
				return false;
			}
			item.baseData.name = scrubUrl(name);
			item.baseData.target = scrubUrl(target);
			item.baseData.data = scrubUrl(data);
		}

		if (item.baseType === 'PageviewData') {
			const { pathname } = new URL(item.baseData?.uri || window.location.href, window.location.origin);
			// setParam re-navigates to add query params, which would otherwise record the same page twice.
			if (pathname === lastPageViewPathname) return false;
			lastPageViewPathname = pathname;
			item.baseData.uri = scrubUrl(item.baseData.uri);
			item.baseData.refUri = scrubReferrer(item.baseData.refUri);
		}

		// The operation name is derived from the URL path, which holds the token on resume routes.
		if (item.ext?.trace?.name) {
			item.ext.trace.name = scrubUrl(item.ext.trace.name);
		}

		item.tags = item.tags || {};
		item.tags['ai.cloud.role'] = CLOUD_ROLE_NAME;
		// Explicit properties passed by the caller win over the shared context.
		item.data = {
			...getBaseContext(),
			...(item.baseType === 'ExceptionData' ? getPolicyContext() : {}),
			...item.data
		};
	} catch (error) {
		// Enrichment problems must never block telemetry, least of all the exception being reported.
	}
	return true;
};

// Called once from src/index.js before React renders, so bootstrap and render errors are captured too.
export const initLogger = () => {
	try {
		if (appInsights || !isConfigured(parameters.appInsightsConnectionString)) return;

		const instance = new ApplicationInsights({
			config: {
				connectionString: parameters.appInsightsConnectionString,
				// No ai_user/ai_session cookies, so telemetry does not depend on cookie consent;
				// journeys are correlated with clientSessionId and the policy references instead.
				disableCookiesUsage: true,
				enableAutoRouteTracking: true,
				enableUnhandledPromiseRejectionTracking: true,
				// The API sits on another origin behind APIM, whose CORS policy does not allow the correlation
				// headers; enabling this would fail every preflight and break all API calls.
				enableCorsCorrelation: false
			}
		});
		instance.loadAppInsights();
		instance.addTelemetryInitializer(telemetryInitializer);
		instance.trackPageView();
		appInsights = instance;
	} catch (error) {
		// Logging must never stop the app from starting.
		appInsights = null;
	}
};

// Every log name is namespaced here, so callers pass short names ('Checkout.EmbeddedFormShown') and all brand-label
// logs can be filtered with one prefix; names that already carry it are left as they are.
const LOG_NAME_PREFIX = 'Brandlabel';
const withPrefix = name => (String(name).startsWith(`${LOG_NAME_PREFIX}.`) ? name : `${LOG_NAME_PREFIX}.${name}`);

// Without a connection string, dev and uat mirror logs to the console instead, so they can still be checked in the
// browser (locally too, via the commented environment: 'dev' line in parameter.js); production stays silent.
const logToConsole = (method, name, properties) => {
	if (['dev', 'uat'].includes(parameters.environment)) {
		console[method](`[logger] ${name}`, properties);
	}
};

const track = (severityLevel, consoleMethod, name, properties = {}) => {
	try {
		const fullName = withPrefix(name);
		if (!appInsights) {
			logToConsole(consoleMethod, fullName, properties);
			return;
		}
		appInsights.trackTrace({ message: fullName, severityLevel }, properties);
	} catch (error) {
		// Logging must never break the calling flow.
	}
};

export const logInfo = (name, properties) => track(SeverityLevel.Information, 'info', name, properties);

export const logWarn = (name, properties) => track(SeverityLevel.Warning, 'warn', name, properties);

// With an exception object the error is recorded as an exception (with its stack). Error conditions without one
// (e.g. a failed API result) are logged as Error-severity traces, so they sit next to the other logs instead of
// creating synthetic exceptions that all group under the logger's own stack frame.
export const logError = (name, properties = {}, error = undefined) => {
	if (!error) {
		track(SeverityLevel.Error, 'error', name, properties);
		return;
	}
	try {
		const fullName = withPrefix(name);
		if (!appInsights) {
			logToConsole('error', fullName, { error, ...properties });
			return;
		}
		// Non-Error values are wrapped so App Insights still records a message and stack.
		const exception = error instanceof Error ? error : new Error(String(error));
		appInsights.trackException({
			exception,
			severityLevel: SeverityLevel.Error,
			properties: { name: fullName, ...properties }
		});
	} catch (trackingError) {
		// Logging must never break the calling flow.
	}
};

// Sends buffered telemetry before hard navigations (gateway redirects, reloads); the unload beacon and the
// session-storage buffer cover anything still in flight.
export const flushLogs = () => {
	try {
		appInsights?.flush();
	} catch (error) {
		// Logging must never break the calling flow.
	}
};

// Safe facts about how the user landed on the site, used to diagnose returns from the payment gateway.
export const getNavigationContext = () => {
	const context = {};
	try {
		const searchParams = new URLSearchParams(landingLocation.search);
		context.landingPath = scrubUrl(landingLocation.pathname);
		context.landingQueryParamNames = [...searchParams.keys()].join(',');
		ALLOWED_QUERY_PARAMS.forEach(param => {
			if (searchParams.has(param)) context[`landingQuery_${param}`] = searchParams.get(param);
		});
		context.referrerOrigin = getUrlOrigin(document.referrer);
		// 'back_forward' or 'reload' here means the user pressed back or refreshed instead of a normal redirect.
		context.navigationType = window.performance?.getEntriesByType?.('navigation')?.[0]?.type;
		// Gateways often break out of iframes, and storage can be partitioned inside them.
		context.isInIframe = window.self !== window.top;
	} catch (error) {
		// Partial context is still useful.
	}
	return context;
};
